import { useEffect, useMemo, useState } from "react"

import { SIMULATOR_TYPES } from "@/constants/simulatorTypes"
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SearchInput,
} from "@coval/ui"
import {
  ArrowLeftRight,
  Bot,
  MessageCircle,
  MessageSquare,
  Mic,
  Phone,
  PhoneOutgoing,
  Video,
  X,
  type LucideIcon,
} from "lucide-react"

import { SimulatorType } from "@/types/api/SimulatorType"

export const AGENT_TYPE_ICONS: Partial<Record<SimulatorType, LucideIcon>> = {
  [SimulatorType.VOICE]: Phone,
  [SimulatorType.OUTBOUND_VOICE]: PhoneOutgoing,
  [SimulatorType.OPENAI_REALTIME]: Mic,
  [SimulatorType.GEMINI_REALTIME]: Mic,
  [SimulatorType.GROK_REALTIME]: Mic,
  [SimulatorType.DEEPGRAM_REALTIME]: Mic,
  [SimulatorType.WEBSOCKET]: ArrowLeftRight,
  [SimulatorType.TEXT]: MessageSquare,
  [SimulatorType.CHAT_A2A]: MessageSquare,
  [SimulatorType.SMS]: MessageCircle,
  [SimulatorType.LIVEKIT]: Video,
  [SimulatorType.PIPECAT]: Bot,
}

interface AgentTypeOption {
  type: SimulatorType
  label: string
  description: string
}

interface AgentTypeGroup {
  title: string
  items: readonly AgentTypeOption[]
}

export const AGENT_TYPE_GROUPS: readonly AgentTypeGroup[] = SIMULATOR_TYPES.map(
  (group) => ({
    title: group.group,
    items: group.items.map((item) => ({
      type: item.id,
      label: item.name,
      description: item.description,
    })),
  }),
)

interface AgentTypeModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (type: SimulatorType) => void
  groups?: readonly AgentTypeGroup[]
  currentValue?: SimulatorType
  title?: string
}

export function AgentTypeModal({
  isOpen,
  onClose,
  onSelect,
  groups = AGENT_TYPE_GROUPS,
  currentValue,
  title = "Select Agent Type",
}: AgentTypeModalProps) {
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (isOpen) {
      setQuery("")
    }
  }, [isOpen])

  const handleSelect = (type: SimulatorType) => {
    onSelect(type)
    onClose()
  }

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      return groups
    }
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            item.description.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [groups, query])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[80vh] w-full max-w-4xl"
        data-testid="agent-type-modal"
      >
        <DialogHeader className="border-b border-coval-border pb-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-base">{title}</DialogTitle>
            <DialogDescription className="sr-only">
              Choose the simulator type that matches how Coval connects to your
              agent.
            </DialogDescription>
            <div className="flex items-center gap-2">
              <SearchInput
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClear={() => setQuery("")}
                placeholder="Search agent types..."
                className="w-64"
              />
              <Button
                variant="secondary"
                icon
                onClick={onClose}
                tooltip="Close"
              >
                <X />
              </Button>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="overflow-y-auto p-6">
          <div className="space-y-6">
            {filteredGroups.length === 0 && (
              <div className="py-8 text-center text-sm text-coval-text-muted">
                No agent types match &ldquo;{query.trim()}&rdquo;
              </div>
            )}
            {filteredGroups.map((group) => (
              <div key={group.title}>
                <h3 className="mb-3 mt-2 text-sm font-semibold text-coval-text">
                  {group.title}
                </h3>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {group.items.map((item) => {
                    const Icon = AGENT_TYPE_ICONS[item.type]
                    const isSelected = item.type === currentValue
                    return (
                      <button
                        key={item.type}
                        type="button"
                        data-testid={`agent-type-option-${item.type}`}
                        onClick={() => handleSelect(item.type)}
                        className={cn(
                          "rounded-lg border p-3 text-left transition-colors hover:bg-surface-default-hover",
                          isSelected
                            ? "border-coval-info-border bg-coval-info-surface"
                            : "border-coval-border bg-surface-default",
                        )}
                      >
                        {Icon && (
                          <Icon
                            className={cn(
                              "mb-2 h-4 w-4",
                              isSelected
                                ? "text-coval-info-on-surface"
                                : "text-coval-text-muted",
                            )}
                          />
                        )}
                        <div
                          className={cn(
                            "text-sm font-medium",
                            isSelected
                              ? "text-coval-info-on-surface"
                              : "text-coval-text",
                          )}
                        >
                          {item.label}
                        </div>
                        <div className="mt-1 text-xs leading-relaxed text-coval-text-muted">
                          {item.description}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
